import React, { useState, useEffect, useRef } from 'react';
import { fetchAPI } from '../../utils/api';
import {
    FiCamera, FiUser, FiCheckCircle, FiXCircle,
    FiClock, FiUsers, FiBook, FiList, FiRefreshCw, FiCalendar
} from 'react-icons/fi';
import { decryptQR } from '../../utils/security';
import jsQR from 'jsqr';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdminAttendance() {
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [history, setHistory] = useState([]);
    const [scanning, setScanning] = useState(false);
    const isScanningRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [lastScan, setLastScan] = useState(null);
    const [error, setError] = useState(null);
    const [manualIndex, setManualIndex] = useState('');

    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        loadClasses();
        loadHistory();
        return () => {
            isScanningRef.current = false;
            stopScanner();
        };
    }, []);

    const loadClasses = async () => {
        try {
            const data = await fetchAPI('/admin/get_classes.php');
            setClasses(data);
            if (data.length > 0) setSelectedClass(data[0].id);
        } catch (err) {
            console.error("Failed to load classes", err);
        }
    };

    const loadHistory = async () => {
        try {
            const data = await fetchAPI(`/admin/get_attendance_history.php?class_id=${selectedClass}&date=${selectedDate}`);
            setHistory(data);
        } catch (err) {
            console.error("Failed to load history", err);
        }
    };

    useEffect(() => {
        if (selectedClass || selectedDate) loadHistory();
    }, [selectedClass, selectedDate]);

    const startScanner = async () => {
        try {
            setError(null);
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
            });

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                isScanningRef.current = true;
                setScanning(true);

                // Ensure video is playing before starting loop
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play();
                    requestAnimationFrame(tick);
                };
            }
        } catch (err) {
            console.error("Camera Error:", err);
            setError("Camera access denied or not available. Please ensure you are using HTTPS.");
            setScanning(false);
            isScanningRef.current = false;
        }
    };

    const stopScanner = () => {
        isScanningRef.current = false;
        setScanning(false);
        if (videoRef.current && videoRef.current.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
    };

    const tick = () => {
        if (!isScanningRef.current) return;

        if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
            const canvas = canvasRef.current;
            const video = videoRef.current;
            if (!canvas) return;

            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            canvas.height = video.videoHeight;
            canvas.width = video.videoWidth;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, imageData.width, imageData.height, {
                inversionAttempts: 'attemptBoth',
            });

            if (code && code.data) {
                console.log("Found QR:", code.data);
                const debugEl = document.getElementById('debug-last-scan');
                if (debugEl) debugEl.innerText = "• Last Detection: " + code.data;
                handleScan(code.data);
            }
        }

        if (isScanningRef.current) {
            requestAnimationFrame(tick);
        }
    };

    const handleScan = async (codeValue) => {
        if (loading) return;

        try {
            // Decrypt our secure format
            const studentData = decryptQR(codeValue);

            if (!studentData || !studentData.i) {
                // Check if it's a legacy JSON format (optional, for transition)
                let legacyData = null;
                if (codeValue.startsWith('{')) {
                    try { legacyData = JSON.parse(codeValue); } catch (e) { }
                }

                if (legacyData && legacyData.i) {
                    console.log("Legacy QR detected");
                    await markAttendance(legacyData.i);
                    return;
                }

                console.warn("Invalid or unencrypted QR:", codeValue);
                setError("Invalid ID Card detected.");
                return;
            }

            console.log("Secure QR Match:", studentData.i);
            const success = await markAttendance(studentData.i);

            if (success && navigator.vibrate) navigator.vibrate(100);

        } catch (err) {
            console.error("Scan processing error:", err);
            setError("Scan Error: " + (err.message || "Invalid QR format"));
            // Cooldown on error to prevent alert spam
            setLoading(true);
            setTimeout(() => setLoading(false), 2000);
            return;
        } finally {
            setLoading(false);
        }
    };

    const markAttendance = async (indexNumber, isManual = false) => {
        if (!selectedClass) {
            setError("Please select a class first");
            return;
        }

        try {
            // Avoid double scanning same person immediately (5 second cooldown)
            // Skip cooldown if it's a manual mark
            if (!isManual && lastScan && lastScan.student.index_number === indexNumber && (Date.now() - lastScan.time < 5000)) {
                return;
            }

            setLoading(true);
            setError(null);
            console.log(`${isManual ? 'Manual' : 'Scan'} marking for:`, indexNumber);

            const response = await fetchAPI('/admin/mark_attendance.php', {
                method: 'POST',
                body: JSON.stringify({
                    index_number: indexNumber,
                    class_id: selectedClass
                })
            });

            console.log("Attendance API Response:", response);
            setLastScan({ student: response.student, time: Date.now(), message: response.message });
            loadHistory();
            return response;
        } catch (err) {
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const handleManualMark = async (e) => {
        e.preventDefault();
        if (!manualIndex.trim()) return;

        try {
            await markAttendance(manualIndex.trim(), true);
            setManualIndex(''); // Clear input on success
        } catch (err) {
            console.error("Manual mark error:", err);
            setError(err.message || "Failed to mark attendance manually");
        }
    };

    return (
        <div className="space-y-4 md:space-y-6">
            {/* Header Section */}
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2 flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-md">
                            <FiCamera size={16} />
                        </div>
                        Attendance Manager
                    </h1>
                    <p className="text-gray-500 font-medium ml-10">Scan student QR codes or manually mark attendance.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full max-w-5xl">
                <form onSubmit={handleManualMark} className="relative group">
                    <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={manualIndex}
                        onChange={(e) => setManualIndex(e.target.value.toUpperCase())}
                        placeholder="MANUAL ID"
                        className="w-full pl-10 pr-16 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-semibold focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
                    />
                    <button
                        type="submit"
                        disabled={loading || !manualIndex.trim()}
                        className="absolute right-1 top-1 bottom-1 px-3 bg-gray-900 text-white rounded-md text-[9px] font-bold uppercase tracking-wider hover:bg-black transition-all disabled:opacity-50 z-10"
                    >
                        {loading && manualIndex ? '...' : 'Mark'}
                    </button>
                </form>

                <div className="relative group">
                    <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-semibold text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all outline-none appearance-none"
                    />
                </div>

                <div className="relative group">
                    <FiBook className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                        className="w-full pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-semibold text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all outline-none appearance-none"
                    >
                        <option value="">Select Class</option>
                        {classes.map(cls => (
                            <option key={cls.id} value={cls.id}>Grade {cls.grade} - {cls.name}</option>
                        ))}
                    </select>
                </div>

                <button
                    onClick={scanning ? stopScanner : startScanner}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-lg font-bold uppercase tracking-wider text-[10px] transition-all duration-300 shadow-md ${scanning
                        ? 'bg-red-500 text-white shadow-red-100 hover:bg-red-600'
                        : 'bg-blue-600 text-white shadow-blue-100 hover:bg-blue-700'
                        }`}
                >
                    {scanning ? <><FiXCircle /> Stop Scanner</> : <><FiCamera /> Launch Scanner</>}
                </button>
            </div>


            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Scanner Section */}
                <div className="lg:col-span-5 h-full">
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col items-center">
                        <div className="w-full relative aspect-square bg-gray-900 rounded-lg overflow-hidden shadow-md border-2 border-white group">
                            {!scanning && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                                    <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mb-4 backdrop-blur-md">
                                        <FiCamera size={32} className="text-gray-400" />
                                    </div>
                                    <p className="text-gray-400 font-black uppercase tracking-widest text-xs">Camera Offline</p>
                                    <button
                                        onClick={startScanner}
                                        className="mt-6 text-blue-500 font-bold hover:text-blue-400 transition-colors"
                                    >
                                        Tap to connect
                                    </button>
                                </div>
                            )}

                            <video ref={videoRef} autoPlay muted playsInline className={`w-full h-full object-cover ${scanning ? 'block' : 'hidden'}`} />
                            <canvas ref={canvasRef} className="hidden" />

                            {scanning && (
                                <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <div className="relative aspect-square border-2 border-blue-500/30 rounded-3xl">
                                        <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-xl"></div>
                                        <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-xl"></div>
                                        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-xl"></div>
                                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-xl"></div>

                                        {/* Scanner Line Animation */}
                                        <motion.div
                                            animate={{ top: ['0%', '100%', '0%'] }}
                                            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                                            className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                                        />
                                    </div>
                                </div>
                            )}

                            {loading && (
                                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
                                    <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            )}
                        </div>

                        {/* Recent Scan Result Card */}
                        <AnimatePresence>
                            {lastScan && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="w-full mt-4 bg-gray-900 rounded-lg p-5 text-white shadow-lg"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                                            <FiCheckCircle size={20} className="text-green-400" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Last Mark</p>
                                            <h4 className="font-bold text-base uppercase truncate">{lastScan.student?.name || 'Success'}</h4>
                                            <div className="flex gap-2 mt-1">
                                                <span className="text-[9px] font-mono text-gray-400">{lastScan.student?.index_number}</span>
                                            </div>
                                            <p className="text-[10px] text-blue-400 mt-1">{lastScan.message}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {error && (
                            <div className="mt-6 flex flex-col gap-2 w-full">
                                <div className="flex items-center gap-3 text-red-600 bg-red-50 p-4 rounded-2xl border border-red-100">
                                    <FiXCircle size={20} />
                                    <span className="text-sm font-bold uppercase tracking-wider">{error}</span>
                                </div>
                                <div className="bg-gray-900 text-gray-400 p-4 rounded-2xl text-[10px] font-mono overflow-auto max-h-32">
                                    <p className="text-blue-400 mb-1 tracking-widest font-black uppercase text-[8px]">Device Status Log</p>
                                    <p>• URL: {window.location.host}</p>
                                    <p>• Is Secure: {window.isSecureContext ? 'Yes' : 'No'}</p>
                                    <p>• Scanning: {scanning ? 'Active' : 'Idle'}</p>
                                    <p id="debug-last-scan">• Last Detection: None</p>
                                    <p>• Canvas: {canvasRef.current ? 'Ready' : 'Waiting'}</p>
                                    <p>• Camera: {videoRef.current?.srcObject ? 'Connected' : 'Disconnected'}</p>
                                </div>
                            </div>
                        )}

                        {!error && scanning && (
                            <div className="mt-6 w-full bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-ping"></span>
                                    Scanner Engine Ready
                                </p>
                                <p className="text-[10px] text-gray-500 font-bold leading-relaxed">
                                    Hold a QR code steady in front of the camera. The scanner will automatically detect and process the attendance.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* History Section */}
                <div className="lg:col-span-7 space-y-4">
                    <div className="bg-white rounded-xl p-5 md:p-6 shadow-sm border border-gray-100 min-h-full flex flex-col">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center text-gray-600 border border-gray-100">
                                    <FiList size={16} />
                                </div>
                                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                                    Attendance Log
                                </h2>
                            </div>
                            <button
                                onClick={loadHistory}
                                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                                <FiRefreshCw size={14} />
                            </button>
                        </div>

                        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-4 scrollbar-thin">
                            {history.length === 0 ? (
                                <div className="text-center py-20 bg-gray-50/50 rounded-[2rem] border border-dashed border-gray-200">
                                    <FiUsers size={48} className="mx-auto text-gray-300 mb-4" />
                                    <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">No records found for today</p>
                                </div>
                            ) : (
                                history.map((record, idx) => (
                                    <motion.div
                                        key={record.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="flex items-center justify-between p-3.5 bg-gray-50/70 hover:bg-white rounded-xl border border-transparent hover:border-gray-100 transition-all duration-200 group"
                                    >
                                        <div className="flex-1 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-blue-600 text-sm font-bold shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors flex-shrink-0">
                                                    {(record.student_name || '?').charAt(0)}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="font-bold text-gray-900 text-sm uppercase truncate">{record.student_name || 'Generic Student'}</h4>
                                                        {record.student_grade && (
                                                            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[8px] font-bold uppercase rounded-md">
                                                                G{record.student_grade}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-400 font-medium text-[9px] uppercase tracking-wider mt-0.5">
                                                        <span className="flex items-center gap-1"><FiUser size={10} /> {record.student_index || record.index_number}</span>
                                                        <span className="flex items-center gap-1"><FiClock size={10} /> {new Date(record.marked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 self-end sm:self-center">
                                                <div className="px-3 py-1 bg-green-50 text-green-600 rounded-md text-[9px] font-bold uppercase border border-green-100">
                                                    {record.status}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
}
